import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams, Link } from "react-router";
import { useNotify } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from "@hello-pangea/dnd";
import {
  ChevronDown,
  Columns3,
  MoreHorizontal,
  Plus,
  Search,
  Table2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { MobilePage } from "../MobilePage";
import { NoOffersNotice } from "../freelancerNotices";
import { startCallTimer } from "../calls/callTimer";
import { telHref } from "../contactLinks";
import { StagePill } from "../ui";
import { offerColor, useOffers, type Offer } from "../useOffers";
import { stageColor, stageLabel } from "./useOfferStages";
import {
  lastContact,
  money,
  useBoard,
  useSaveDeal,
  type DealRow,
} from "./boardData";
import { LossDialog, type LossTarget } from "./LossDialog";
import { DealForm, type DealFormTarget } from "./DealForm";
import { DeleteDealDialog } from "./DeleteDealDialog";
import { OfferForm, type OfferFormTarget } from "./OfferForm";
import { useIsDueno } from "../useIsDueno";
import { PriorityIcon, ThermoBar, Trend } from "../thermo/thermo";
import { HelpTooltip } from "../help/HelpTooltip";

const LAST_KEY = "raftel.board.last";
const pct = (x: number) => `${Math.round(x * 100)}%`;
const isOpen = (d: DealRow, o: Offer) =>
  d.stage !== o.won_stage && d.stage !== o.lost_stage;

/** /board → último tablero usado (o el primero). */
export const BoardIndex = () => {
  const { data: offers, isPending } = useOffers();
  if (isPending) return null;
  const last = Number(localStorage.getItem(LAST_KEY));
  const id = offers?.find((o) => o.id === last)?.id ?? offers?.[0]?.id;
  return id ? (
    <Navigate to={`/board/${id}`} replace />
  ) : (
    <div className="p-4">
      <NoOffersNotice />
    </div>
  );
};

/** Tablero por producto (PRD B.1): Tabla por grupos de etapa y Kanban. */
export const OfferBoard = () => {
  const { offerId } = useParams();
  const id = Number(offerId);
  const { data: offers } = useOffers();
  const offer = offers?.find((o) => o.id === id);
  const { data: deals, isPending } = useBoard(id);
  const isMobile = useIsMobile();
  const [mode, setMode] = useState<"tabla" | "kanban">("tabla");
  const [q, setQ] = useState("");
  const [form, setForm] = useState<DealFormTarget | null>(null);
  const [loss, setLoss] = useState<LossTarget | null>(null);
  const [del, setDel] = useState<DealRow | null>(null);
  const [offerForm, setOfferForm] = useState<OfferFormTarget | null>(null);
  const isDueno = useIsDueno();

  useEffect(() => {
    if (id) localStorage.setItem(LAST_KEY, String(id));
  }, [id]);

  const rows = useMemo(
    () =>
      (deals ?? []).filter(
        (d) =>
          !q ||
          d.company_name.toLowerCase().includes(q.toLowerCase()) ||
          d.key?.toLowerCase().includes(q.toLowerCase()),
      ),
    [deals, q],
  );

  if (!offer)
    return offers ? (
      <p className="p-4 text-muted-foreground">Tablero no encontrado.</p>
    ) : null;

  const open = (deals ?? []).filter((d) => isOpen(d, offer));
  const actions: Actions = {
    edit: (d) => setForm({ mode: "edit", deal: d }),
    lose: (d) =>
      setLoss({
        id: d.id,
        key: d.key,
        company_name: d.company_name,
        offer_id: d.offer_id,
      }),
    remove: (d) => setDel(d),
    add: (stage) => setForm({ mode: "create", offerId: offer.id, stage }),
    other: (d) =>
      setForm({
        mode: "create",
        offerId: offers!.find((o) => o.id !== d.offer_id)!.id,
        companyId: d.company_id,
      }),
  };

  return (
    <MobilePage title={offer.name}>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-start gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="flex items-center gap-2.5 text-2xl font-extrabold tracking-tight max-md:hidden">
              <span
                className="size-3.5 rounded"
                style={{ background: offerColor(offer) }}
              />
              {offer.name}
              <HelpTooltip topic="pipeline.board" />
            </h1>
            <p className="text-sm text-muted-foreground">
              {open.length} abiertas · valor en juego{" "}
              {money(open.reduce((s, d) => s + d.amount, 0))} · pronóstico
              ponderado{" "}
              {money(
                Math.round(open.reduce((s, d) => s + d.amount * d.prob, 0)),
              )}
            </p>
          </div>
          {/* the offer catalog is read-only for freelancers (E6.4) */}
          {isDueno && (
            <Button
              variant="outline"
              onClick={() => setOfferForm({ mode: "edit", offer })}
            >
              Editar tablero
            </Button>
          )}
        </div>

        <div className="flex border-b">
          {(
            [
              ["tabla", isMobile ? "Lista" : "Tabla principal", Table2],
              ["kanban", "Kanban", Columns3],
            ] as const
          ).map(([m, label, Icon]) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={cn(
                "flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-semibold",
                mode === m
                  ? "border-primary"
                  : "border-transparent text-muted-foreground",
              )}
            >
              <Icon className="size-4" />
              {label}
            </button>
          ))}
          <Link
            to="/deals"
            className="ml-auto self-center text-sm text-muted-foreground max-md:hidden"
          >
            Ver todas las ofertas
          </Link>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => actions.add()}>
            <Plus />
            Nueva oportunidad
          </Button>
          <label className="flex h-9 flex-1 items-center gap-2 rounded-lg border bg-card px-2.5 sm:max-w-72">
            <Search className="size-4 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar empresa o clave"
              className="w-full bg-transparent text-sm outline-none"
            />
          </label>
        </div>

        {isPending ? (
          <Skeleton className="h-40 w-full" />
        ) : mode === "kanban" ? (
          <Kanban offer={offer} rows={rows} actions={actions} />
        ) : (
          offer.stages.map((st) => (
            <StageGroup
              key={st}
              offer={offer}
              stage={st}
              rows={rows.filter((d) => d.stage === st)}
              actions={actions}
              mobile={isMobile}
            />
          ))
        )}
      </div>

      <DealForm
        target={form}
        onClose={() => setForm(null)}
        onLose={setLoss}
        onDelete={setDel}
      />
      <LossDialog target={loss} onClose={() => setLoss(null)} />
      <DeleteDealDialog
        deal={del}
        lostStage={offer.lost_stage}
        onClose={() => setDel(null)}
        onLose={actions.lose}
      />
      <OfferForm target={offerForm} onClose={() => setOfferForm(null)} />
    </MobilePage>
  );
};

type Actions = {
  edit: (d: DealRow) => void;
  lose: (d: DealRow) => void;
  remove: (d: DealRow) => void;
  add: (stage?: string) => void;
  other: (d: DealRow) => void;
};

/** Cambiar etapa: si es Perdido, primero el motivo (y si cancela, no cambia). */
const useSetStage = (offer: Offer, lose: (d: DealRow) => void) => {
  const { save } = useSaveDeal();
  const notify = useNotify();
  return async (d: DealRow, stage: string) => {
    if (stage === d.stage) return;
    if (stage === offer.lost_stage) return lose(d);
    try {
      await save(d.id, { stage });
      notify(`${d.key} · ${stageLabel(stage)}`, { type: "success" });
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    }
  };
};

const StagePicker = ({
  offer,
  deal,
  onPick,
  className,
}: {
  offer: Offer;
  deal: DealRow;
  onPick: (s: string) => void;
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn("cursor-pointer hover:brightness-95", className)}
          aria-label={`Etapa de ${deal.key}: ${stageLabel(deal.stage)}`}
        >
          <StagePill stage={deal.stage} className="w-full min-w-24 py-1.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="grid w-44 gap-1 p-1.5" align="start">
        {offer.stages.map((s) => (
          <button
            key={s}
            onClick={() => {
              setOpen(false);
              onPick(s);
            }}
          >
            <StagePill stage={s} className="w-full py-1.5" />
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};

const DealMenu = ({
  deal,
  offer,
  actions,
}: {
  deal: DealRow;
  offer: Offer;
  actions: Actions;
}) => {
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          aria-label={`Acciones de ${deal.key}`}
        >
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {deal.phone && !deal.dnc && (
          <DropdownMenuItem
            onSelect={() => {
              startCallTimer();
              window.location.href = telHref(deal.phone)!;
            }}
          >
            Llamar
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={() => actions.edit(deal)}>
          Editar
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate(`/companies/${deal.company_id}/show`)}
        >
          Abrir ficha
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => actions.other(deal)}>
          Abrir en otro producto
        </DropdownMenuItem>
        {deal.stage !== offer.lost_stage && (
          <DropdownMenuItem onSelect={() => actions.lose(deal)}>
            Marcar como perdida
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          className="text-destructive"
          onSelect={() => actions.remove(deal)}
        >
          Eliminar
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** Valor y fecha editables en línea: se guardan al salir del campo. */
const InlineMoney = ({ deal }: { deal: DealRow }) => {
  const { save } = useSaveDeal();
  const [v, setV] = useState(
    deal.amount ? deal.amount.toLocaleString("es-MX") : "",
  );
  useEffect(
    () => setV(deal.amount ? deal.amount.toLocaleString("es-MX") : ""),
    [deal.amount],
  );
  const commit = () => {
    const n = Number(v.replace(/\D/g, "")) || 0;
    if (n !== deal.amount) save(deal.id, { amount: n });
  };
  return (
    <input
      value={v}
      inputMode="numeric"
      placeholder="—"
      aria-label={`Valor de ${deal.key}`}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) =>
        e.key === "Enter" && (e.target as HTMLInputElement).blur()
      }
      className="w-28 rounded-md border border-transparent bg-transparent px-1.5 py-1 text-right hover:border-border focus:border-border focus:bg-card"
    />
  );
};

const InlineDate = ({ deal }: { deal: DealRow }) => {
  const { save } = useSaveDeal();
  return (
    <input
      type="date"
      value={deal.next_step_at ?? ""}
      aria-label={`Próximo paso de ${deal.key}`}
      onChange={(e) => save(deal.id, { next_step_at: e.target.value || null })}
      className="w-36 rounded-md border border-transparent bg-transparent px-1.5 py-1 hover:border-border focus:border-border focus:bg-card"
    />
  );
};

const Cadence = ({ d }: { d: DealRow }) =>
  d.cad_total ? (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary"
          style={{ width: `${(d.cad_done / d.cad_total) * 100}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground">
        {d.cad_done}/{d.cad_total}
      </span>
    </div>
  ) : (
    <span className="text-muted-foreground">—</span>
  );

const initials = (d: DealRow) =>
  `${d.sales_first_name?.[0] ?? ""}${d.sales_last_name?.[0] ?? ""}`.toUpperCase() ||
  "—";

const StageGroup = ({
  offer,
  stage,
  rows,
  actions,
  mobile,
}: {
  offer: Offer;
  stage: string;
  rows: DealRow[];
  actions: Actions;
  mobile: boolean;
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const setStage = useSetStage(offer, actions.lose);
  const open = stage !== offer.won_stage && stage !== offer.lost_stage;
  const total = rows.reduce((s, d) => s + d.amount, 0);
  const forecast = Math.round(rows.reduce((s, d) => s + d.amount * d.prob, 0));
  const withThermo = rows.filter((d) => d.thermo != null);
  const avgThermo = withThermo.length
    ? Math.round(
        withThermo.reduce((s, d) => s + d.thermo!, 0) / withThermo.length,
      )
    : null;
  const color = `var(${stageColor(stage)[0]})`;

  return (
    <section className="mt-2">
      <button
        onClick={() => setCollapsed(!collapsed)}
        aria-expanded={!collapsed}
        className="flex items-center gap-2 py-1.5 text-[15px] font-extrabold"
      >
        <ChevronDown
          className={cn(
            "size-4 transition-transform",
            collapsed && "-rotate-90",
          )}
        />
        <StagePill stage={stage} />
        <span className="text-sm font-semibold text-muted-foreground">
          {rows.length} {rows.length === 1 ? "elemento" : "elementos"}
        </span>
      </button>
      {!collapsed &&
        (mobile ? (
          <div className="flex flex-col gap-2">
            {rows.map((d) => (
              <Card
                key={d.id}
                className="gap-2 p-3"
                style={{ borderLeft: `5px solid ${color}` }}
              >
                <div className="flex items-start justify-between gap-2">
                  <Link
                    to={`/companies/${d.company_id}/show`}
                    className="min-w-0"
                  >
                    <span className="flex items-center gap-1 font-mono text-xs text-muted-foreground">
                      <PriorityIcon
                        thermo={d.thermo}
                        offerPriority={d.offer_priority}
                      />
                      {d.key}
                    </span>
                    <span className="block truncate font-bold">
                      {d.company_name}
                    </span>
                  </Link>
                  <DealMenu deal={d} offer={offer} actions={actions} />
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <StagePicker
                    offer={offer}
                    deal={d}
                    onPick={(s) => setStage(d, s)}
                  />
                  <ThermoBar value={d.thermo} mini />
                  <span className="text-muted-foreground">
                    Prob. {pct(d.prob)}
                  </span>
                  <span className="ml-auto font-bold">{money(d.amount)}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                  Próximo paso <InlineDate deal={d} />
                  <span
                    className={cn(
                      "ml-auto",
                      open &&
                        (d.thermo_decay ?? 0) > 0 &&
                        "font-bold text-destructive",
                    )}
                  >
                    {lastContact(d.idle_days)}
                  </span>
                </div>
              </Card>
            ))}
            <Button
              variant="ghost"
              className="self-start text-muted-foreground"
              onClick={() => actions.add(stage)}
            >
              + Agregar oportunidad
            </Button>
            {rows.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Pronóstico {money(forecast)} · valor {money(total)}
              </p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border bg-card">
            <table className="w-full min-w-[980px] table-fixed text-sm">
              <colgroup>
                {[21, 11, 13, 6, 12, 10, 9, 9, 5, 4].map((w, i) => (
                  <col key={i} style={{ width: `${w}%` }} />
                ))}
              </colgroup>
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-[var(--faint)]">
                  <th className="px-3 py-2">Empresa</th>
                  <th className="px-3 py-2">Etapa</th>
                  <th className="px-3 py-2">Termómetro</th>
                  <th className="px-3 py-2" title="Probabilidad de cierre">
                    Prob.
                  </th>
                  <th className="px-3 py-2">Próximo paso</th>
                  <th className="px-3 py-2 text-right">Valor (MXN)</th>
                  <th className="px-3 py-2">Cadencia</th>
                  <th className="px-3 py-2">Últ. contacto</th>
                  <th className="px-3 py-2">Persona</th>
                  <th className="px-3 py-2">
                    <span className="sr-only">Acciones</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id} className="border-b hover:bg-secondary/60">
                    <td
                      className="px-3 py-2"
                      style={{ borderLeft: `5px solid ${color}` }}
                    >
                      <div className="flex items-center gap-2">
                        <PriorityIcon
                          thermo={d.thermo}
                          offerPriority={d.offer_priority}
                        />
                        <span className="shrink-0 whitespace-nowrap font-mono text-xs text-muted-foreground">
                          {d.key}
                        </span>
                        <Link
                          to={`/companies/${d.company_id}/show`}
                          className="truncate font-bold hover:text-primary hover:underline"
                        >
                          {d.company_name}
                        </Link>
                      </div>
                      {d.city && (
                        <div className="text-xs text-muted-foreground">
                          {d.city}
                        </div>
                      )}
                    </td>
                    <td className="w-36 p-0">
                      <StagePicker
                        offer={offer}
                        deal={d}
                        onPick={(s) => setStage(d, s)}
                        className="block w-full [&>span]:rounded-none [&>span]:py-3"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <ThermoBar value={d.thermo} mini />
                      <div>
                        <Trend
                          weeks={d.trend_weeks}
                          value={d.thermo}
                          change={d.trend_2w}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2 font-bold">{pct(d.prob)}</td>
                    <td className="px-3 py-2">
                      <InlineDate deal={d} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <InlineMoney deal={d} />
                    </td>
                    <td className="px-3 py-2">
                      <Cadence d={d} />
                    </td>
                    <td
                      className={cn(
                        "px-3 py-2 text-muted-foreground",
                        open &&
                          (d.thermo_decay ?? 0) > 0 &&
                          "font-bold text-destructive",
                      )}
                    >
                      {lastContact(d.idle_days)}
                    </td>
                    <td className="px-3 py-2">
                      <span className="grid size-7 place-items-center rounded-full bg-primary text-[11px] font-extrabold text-primary-foreground">
                        {initials(d)}
                      </span>
                    </td>
                    <td className="px-2 py-2">
                      <DealMenu deal={d} offer={offer} actions={actions} />
                    </td>
                  </tr>
                ))}
                <tr className="border-b">
                  <td
                    colSpan={10}
                    className="px-3 py-1.5"
                    style={{ borderLeft: `5px solid ${color}` }}
                  >
                    <button
                      className="text-sm font-semibold text-muted-foreground hover:text-primary"
                      onClick={() => actions.add(stage)}
                    >
                      + Agregar oportunidad
                    </button>
                  </td>
                </tr>
                <tr className="bg-secondary font-bold text-muted-foreground">
                  <td
                    className="px-3 py-2"
                    style={{ borderLeft: `5px solid ${color}` }}
                  >
                    {rows.length} en total
                  </td>
                  <td />
                  <td className="px-3 py-2" title="Promedio del termómetro">
                    {avgThermo != null && <ThermoBar value={avgThermo} mini />}
                  </td>
                  <td
                    colSpan={2}
                    className="px-3 py-2"
                    title="Suma de valor × probabilidad"
                  >
                    {rows.length ? `Pronóstico ${money(forecast)}` : ""}
                  </td>
                  <td className="px-3 py-2 text-right">{money(total)}</td>
                  <td colSpan={4} />
                </tr>
              </tbody>
            </table>
          </div>
        ))}
    </section>
  );
};

/** Kanban: arrastrar cambia la etapa (a Perdido, primero el motivo). */
const Kanban = ({
  offer,
  rows,
  actions,
}: {
  offer: Offer;
  rows: DealRow[];
  actions: Actions;
}) => {
  const queryClient = useQueryClient();
  const setStage = useSetStage(offer, actions.lose);
  const onDragEnd = ({ draggableId, destination }: DropResult) => {
    const d = rows.find((x) => String(x.id) === draggableId);
    if (!d || !destination || destination.droppableId === d.stage) return;
    const stage = destination.droppableId;
    if (stage !== offer.lost_stage) {
      // se mueve al instante; si falla, el refresco lo regresa
      queryClient.setQueryData<DealRow[]>(["deal_board", offer.id], (old) =>
        old?.map((x) => (x.id === d.id ? { ...x, stage } : x)),
      );
    }
    setStage(d, stage);
  };
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="flex gap-3 overflow-x-auto pb-4">
        {offer.stages.map((st) => {
          const list = rows.filter((d) => d.stage === st);
          return (
            <div
              key={st}
              className="flex w-[78vw] shrink-0 flex-col gap-2 rounded-xl bg-secondary p-2.5 sm:w-64"
            >
              <div className="flex items-center gap-2 px-1 font-extrabold">
                <span
                  className="h-4 w-1 rounded"
                  style={{ background: `var(${stageColor(st)[0]})` }}
                />
                {stageLabel(st)}
                <span className="ml-auto text-sm font-semibold text-[var(--faint)]">
                  {list.length}
                </span>
              </div>
              <Droppable droppableId={st}>
                {(p, snap) => (
                  <div
                    ref={p.innerRef as React.Ref<HTMLDivElement>}
                    {...p.droppableProps}
                    className={cn(
                      "flex min-h-60 flex-col gap-2 rounded-lg",
                      snap.isDraggingOver &&
                        "outline-2 outline-dashed outline-primary",
                    )}
                  >
                    {list.map((d, i) => (
                      <Draggable
                        key={d.id}
                        draggableId={String(d.id)}
                        index={i}
                      >
                        {(dp) => (
                          <div
                            ref={dp.innerRef as React.Ref<HTMLDivElement>}
                            {...(dp.draggableProps as unknown as React.HTMLAttributes<HTMLDivElement>)}
                            {...dp.dragHandleProps}
                          >
                            <Card className="gap-1.5 p-2.5">
                              <div className="flex items-start justify-between gap-2">
                                {/* texto, no enlace: un enlace no deja arrastrar la tarjeta */}
                                <span className="min-w-0 font-bold">
                                  {d.company_name}
                                </span>
                                <DealMenu
                                  deal={d}
                                  offer={offer}
                                  actions={actions}
                                />
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="flex items-center gap-1 font-mono text-xs text-muted-foreground">
                                  <PriorityIcon
                                    thermo={d.thermo}
                                    offerPriority={d.offer_priority}
                                  />
                                  {d.key}
                                </span>
                                <ThermoBar value={d.thermo} mini />
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">
                                  Prob. {pct(d.prob)}
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">
                                  Próximo:{" "}
                                  {d.next_step_at
                                    ? d.next_step_at
                                        .split("-")
                                        .reverse()
                                        .slice(0, 2)
                                        .join("/")
                                    : "—"}
                                </span>
                                <b>{money(d.amount)}</b>
                              </div>
                            </Card>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {p.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
};
